import { flatten } from "ramda";
import { type ReactElement, useCallback, useEffect, useMemo, useState, type VFC } from "react";
import { useDispatch, useSelector } from "react-redux";
import { LoadingSpinner } from "widgets";

import { isPlaybookExecutableInPlaybookPageContext, loadPlaybooks } from "@/app-utils/playbook.ts";
import type { Capability, Configuration, Playbook, PlaybookType } from "@/graphql.ts";
import { filterByCapability, type IRestrictedByCapability } from "@/modules/users/utils/user.js";
import { SHOW_NAVBAR_DIALOG } from "@/redux/action-types.ts";
import { filterByLicense, type ILicensable } from "@/redux/modules/config.ts";
import { downloadJnlpFile } from "@/redux/modules/files.ts";
import { openPlaybook } from "@/redux/modules/playbook-execution.ts";
import type { IAppState } from "@/redux/reducer.ts";
import type { ReduxDispatch } from "@/redux/redux-utils.ts";
import { visualizerData } from "@/ui-modules/core/navbar/settings.js";

import GenericCard, { type IGenericCardProps } from "../core/GenericCard.js";
import { GenericTile, PlaybookTile } from "../core/tiles/index.js";
import style from "./styles/Dashboard.module.scss";

export interface IPanelLink extends ILicensable, IRestrictedByCapability {
  title: string;
  url?: string; // if we got a specific link
  updateState?: any; // if we do not hold a link but we change the state e.g. modal dialog
  description: string | ReactElement;
  imgPath?: string;
  img?: string;
  active: boolean;
  jnlpModuleId?: string;
  tooltip?: string;
  playbook?: Playbook;
  type?: PlaybookType;
  tag?: string;
}

const QuicklinkDashboardCard: VFC<IGenericCardProps> = (props) => {
  const dispatch = useDispatch<ReduxDispatch>();
  const [playbooks, setPlaybooks] = useState<Playbook[]>([]);
  const [isLoadingPlaybooks, setLoadingPlaybooks] = useState<boolean>(false);

  const configuration = useSelector<IAppState, Configuration>((state) => state.config.configuration);
  const sessionCapabilities = useSelector<IAppState, Capability[]>((state) => state.auth.session?.capabilities);

  const visualizers = useMemo(() => {
    return flatten(
      visualizerData.map((data) => filterByCapability(filterByLicense(data, configuration), sessionCapabilities)),
    ).filter((panelLink) => panelLink.title);
  }, [configuration, sessionCapabilities]);

  const handleSuggestionSelection = useCallback(
    (item: IPanelLink) => {
      if (item.active) {
        if (item.jnlpModuleId) dispatch(downloadJnlpFile({ moduleId: item.jnlpModuleId }));
        if (item.playbook) dispatch(openPlaybook(item.playbook));
        if (item.updateState) dispatch({ type: SHOW_NAVBAR_DIALOG, payload: item.updateState });
        if (/^https?:\/\//.test(item.url)) window.open(item.url);
      }
    },
    [dispatch],
  );

  const fetchPlaybooks = useCallback(() => {
    setLoadingPlaybooks(true);
    return dispatch(loadPlaybooks()).then((playbooks) => {
      const visiblePlaybooks = playbooks.filter(isPlaybookExecutableInPlaybookPageContext);
      setPlaybooks(visiblePlaybooks);
      setLoadingPlaybooks(false);
    });
  }, []);

  useEffect(() => {
    void fetchPlaybooks();
  }, []);

  return (
    <GenericCard {...props} refreshData={fetchPlaybooks} tipTourId={"quick-links-title"}>
      {isLoadingPlaybooks && <LoadingSpinner inCenter />}
      {!isLoadingPlaybooks && (
        <div className={style.grid}>
          {playbooks?.map((playbook) => <PlaybookTile playbook={playbook} key={playbook.id} />)}
          {visualizers?.map((viz) => (
            <GenericTile
              title={viz.title}
              icon={<img src={viz.imgPath || viz.img} width={75} height={44} alt="" />}
              footNote={viz.tooltip}
              onPress={viz.url ? undefined : () => handleSuggestionSelection(viz)}
              to={viz.url}
              key={viz.title}
            >
              {viz.description}
            </GenericTile>
          ))}
        </div>
      )}
    </GenericCard>
  );
};

export { QuicklinkDashboardCard };
